import type { ReactNode } from 'react';
import { useMyPermissions } from '../api/hooks';
import { EmptyState } from '../../components/ui/EmptyState';

/**
 * O front só manifesta o que a pessoa pode fazer; a autoridade é o backend,
 * que nega com 403 de qualquer forma. Aqui escondemos o que não se aplica.
 */
export function usePermissions() {
  const { data, isLoading, isError } = useMyPermissions();
  const granted = new Set(data ?? []);
  return {
    ready: !isLoading,
    failed: isError,
    can: (permission: string) => granted.has(permission),
  };
}

/** Permissão mínima para abrir cada área do portal. */
export const AREA_PERMISSIONS: Record<string, string> = {
  '/dashboard': 'reports.dashboard.read',
  '/pessoas': 'community.people.read',
  '/calendario': 'calendar.events.read',
  '/tarefas': 'tasks.read',
  '/estoque': 'inventory.items.read',
  '/financeiro': 'finance.entries.read',
  '/obrigacoes': 'obligations.read',
  '/documentos': 'documents.read',
  '/comunicacao': 'communication.messages.read',
  '/cargos': 'functions.read',
  '/permissoes': 'roles.read',
  '/configuracoes': 'house.settings.read',
  '/assinatura': 'house.settings.read',
};

export function firstAllowedArea(can: (permission: string) => boolean): string | null {
  const entry = Object.entries(AREA_PERMISSIONS).find(([, permission]) => can(permission));
  return entry ? entry[0] : null;
}

export function NoAccess() {
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 720 }}>
      <EmptyState
        title="Esta área não faz parte do seu acesso."
        copy="Se precisar dela, fale com a administração da casa."
      />
    </div>
  );
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: string;
  children: ReactNode;
}) {
  const { ready, can } = usePermissions();
  if (!ready) return null;
  return can(permission) ? <>{children}</> : <NoAccess />;
}

/** Valor só quando permitido: evita manifestar ação que o backend negaria. */
export function onlyIf<T>(allowed: boolean, value: T): T | undefined {
  return allowed ? value : undefined;
}
